import { resolveMethodAssignment } from 'features/libra/inspectionPlanning/methodAssignments'
import { InspectionArea } from 'features/libra/inspectionPlanning/planningTypes'
import { TechniqueCatalogue } from 'features/libra/inspectionPlanning/techniqueTypes'
import { drawZonesOnCanvas } from 'features/libra/stepComponents/niiReport/zoneUtils'
import {
  zoneBoxLabel,
  type ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { fetchImageAsBase64 } from 'utils/imageUtils'

import { WorkPackDrawing, WorkPackImage } from './workPackTypes'

export function buildWorkPackDrawings(
  zones: readonly ZoneParams[],
  areas: readonly InspectionArea[],
  catalogue: TechniqueCatalogue
): WorkPackDrawing[] {
  const drawings = new Map<string, WorkPackDrawing>()
  const add = (id: string, mark?: WorkPackDrawing['marks'][number]) => {
    const drawing = drawings.get(id) ?? { id, marks: [] }
    if (mark) drawing.marks.push(mark)
    drawings.set(id, drawing)
  }
  zones.forEach((zone, zoneIndex) => {
    const imageId = zone.zone_reference_image_id
    if (!imageId) return
    const boxes = zone.drawing_bounds ?? []
    if (boxes.length === 0) add(imageId)
    boxes.forEach((drawing_bounds, boxIndex) =>
      add(imageId, {
        title: zoneBoxLabel(`Z${zoneIndex + 1}`, boxIndex),
        description: zone.title,
        color: '#64748B',
        drawing_bounds,
      })
    )
  })
  areas.forEach((area, index) => {
    if (area.zone_reference_image_id)
      add(
        area.zone_reference_image_id,
        area.drawing_bounds
          ? {
              title: `P${index + 1}`,
              description: `${area.title} - ${area.methodAssignments
                .map(
                  (method) => resolveMethodAssignment(method, catalogue).method
                )
                .join(', ')}`,
              color: '#0D9488',
              drawing_bounds: area.drawing_bounds,
            }
          : undefined
      )
  })
  return [...drawings.values()]
}

export async function loadWorkPackImages(
  drawings: readonly WorkPackDrawing[],
  files: readonly { id: string; name: string; url: string }[],
  encode = fetchImageAsBase64
): Promise<WorkPackImage[]> {
  const images: WorkPackImage[] = []
  for (const drawing of drawings) {
    const file = files.find((item) => item.id === drawing.id)
    if (!file)
      throw new Error(
        `Drawing ${drawing.id} is unavailable. Reload its document before exporting.`
      )
    try {
      const dataUrl = await encode(file.url, undefined, (ctx, image) =>
        drawZonesOnCanvas(ctx, image.naturalHeight, drawing.marks)
      )
      images.push({ id: drawing.id, name: file.name, dataUrl })
    } catch {
      throw new Error(
        `Could not load drawing "${file.name}". Check the reference image and retry the export.`
      )
    }
  }
  return images
}
